import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  Briefcase,
  Calendar,
  Code,
  GraduationCap,
  LayoutDashboard,
  LayoutGrid,
  MessageSquare,
  Sparkles,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getUserProfileId } from '../../utils/userProfile';
import {
  canAccessAdmin,
  canManageInterviews,
  canReview,
  canWriteCourses,
  canWriteEvents,
  canWriteGigs,
  canWriteLabs,
  canWriteProjects,
  isAcademyAdmin,
} from '../../utils/roles';

const SHOW_ON = [
  /^\/admin(\/|$)/,
  /^\/my-courses(\/|$)/,
  /^\/dashboard$/,
  /^\/courses(\/|$)/,
  /^\/labs(\/|$)/,
  /^\/projects(\/|$)/,
  /^\/$/,
];

const AdminFab: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const onAdminShell = location.pathname === '/admin/dashboard';
  const visible = !onAdminShell
    && SHOW_ON.some((pattern) => pattern.test(location.pathname))
    && (canAccessAdmin(user) || canWriteCourses(user) || canWriteLabs(user));

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  if (!visible) return null;

  const items = [
    { to: '/admin/dashboard', label: 'Admin dashboard', icon: LayoutDashboard, show: canAccessAdmin(user) },
    { to: '/admin/dashboard?tab=courses', label: 'Courses', icon: BookOpen, show: canWriteCourses(user) },
    { to: '/admin/dashboard?tab=labs', label: 'Labs', icon: Code, show: canWriteLabs(user) },
    { to: '/admin/dashboard?tab=projects', label: 'Projects', icon: Briefcase, show: canWriteProjects(user) },
    { to: '/admin/dashboard?tab=gigs', label: 'Gigs', icon: Briefcase, show: canWriteGigs(user) },
    { to: '/admin/dashboard?tab=review', label: 'Review', icon: Sparkles, show: canReview(user) },
    { to: '/admin/dashboard?tab=school-programs', label: 'IDC Academy', icon: GraduationCap, show: isAcademyAdmin(user) },
    { to: '/admin/dashboard?tab=school-interviews', label: 'Interviews', icon: MessageSquare, show: canManageInterviews(user) },
    { to: '/admin/dashboard?tab=events', label: 'Events', icon: Calendar, show: canWriteEvents(user) },
    { to: '/dashboard', label: 'Learner dashboard', icon: LayoutGrid, show: Boolean(getUserProfileId(user)) },
  ].filter((item) => item.show);

  return (
    <div className="fixed bottom-6 left-4 sm:left-6 z-[55] flex flex-col items-start gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            className="w-60 rounded-2xl border border-gray-200 bg-white shadow-xl p-2"
          >
            <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Admin menu
            </p>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-primary-50 transition-colors"
                >
                  <Icon className="h-4 w-4 text-primary-700 shrink-0" />
                  <span className="text-sm font-medium text-gray-900">{item.label}</span>
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        aria-label={open ? 'Close admin menu' : 'Open admin menu'}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="h-14 w-14 rounded-full bg-gray-900 text-white shadow-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center"
      >
        {open ? <X className="h-6 w-6" /> : <LayoutDashboard className="h-6 w-6" />}
      </button>
    </div>
  );
};

export default AdminFab;
